/* =============================================================================
 * M.M.LaFleur — Editorial layer behaviour
 *
 * Everything here is written as delegated document-level listeners so it keeps
 * working after Barba swaps the <main> container. The only per-page work is in
 * mmInit(), which is idempotent and re-runs on navigation.
 *
 * SINGLE REGISTRATION
 * -------------------
 * Platter re-executes this file around a Barba container swap. Without a guard
 * every listener below is bound a second time after the first soft navigation,
 * and a third time after the next. Anything idempotent survives that — size
 * chips still select, quick add still opens — but every TOGGLE dies, because it
 * fires an even number of times per click and lands back where it started.
 *
 * That is what killed the PLP filter drawer, the facet headers and the PDP
 * accordions after navigating between collections.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmEditorialBound) {
    /* Re-injected after a container swap. The listeners from the first run are
       still live, so only the per-page work needs repeating. */
    if (typeof window.mmInit === "function") window.mmInit();
    return;
  }
  window.__mmEditorialBound = true;

  /* Captured synchronously at load, while document.currentScript is still set.
     ensureSwatchStyles() runs later from an async callback, by which point
     currentScript is null — so the value has to be taken now. */
  var SELF_SRC = (document.currentScript && document.currentScript.src) || "";

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* ---------------------------------------------------------------------- */
  /* Cart helpers                                                            */
  /* ---------------------------------------------------------------------- */

  function refreshCartCount() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$("[data-mm-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count > 0 ? cart.item_count : "";
        });
        return cart;
      })
      .catch(function () { /* non-fatal */ });
  }

  function addToCart(variantId, quantity) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || "Could not add to bag"); });
      return res.json();
    }).then(function (data) {
      refreshCartCount();
      return data;
    });
  }

  function flash(button, label, revertMs) {
    if (!button) return;
    var original = button.getAttribute("data-mm-label") || button.textContent;
    button.setAttribute("data-mm-label", original);
    button.textContent = label;
    window.setTimeout(function () { button.textContent = original; }, revertMs || 1600);
  }

  /* ---------------------------------------------------------------------- */
  /* Delegated clicks                                                        */
  /* ---------------------------------------------------------------------- */

  document.addEventListener("click", function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    /* -- announcement bar ------------------------------------------------ */
    var announceClose = t.closest("[data-mm-announce-close]");
    if (announceClose) {
      var bar = announceClose.closest("[data-mm-announce]");
      if (bar) bar.remove();
      try { sessionStorage.setItem("mm-announce-closed", "1"); } catch (e) {}
      syncHeaderHeight();
      return;
    }

    /* -- mobile drawer --------------------------------------------------- */
    if (t.closest("[data-mm-drawer-open]")) { setDrawer(true); return; }
    if (t.closest("[data-mm-drawer-close]") || t.closest(".mm-drawer-scrim")) { setDrawer(false); return; }

    /* Superseded by mm-drawer-accordion.js, which owns .mm-drawer-toggle on
       the capture phase. Unreachable for those clicks. */
    var drawerToggle = t.closest(".mm-drawer-toggle");
    if (drawerToggle) {
      var expanded = drawerToggle.getAttribute("aria-expanded") === "true";
      drawerToggle.setAttribute("aria-expanded", String(!expanded));
      var panel = drawerToggle.nextElementSibling;
      if (panel) panel.setAttribute("data-open", String(!expanded));
      return;
    }

    /* -- hero slides ----------------------------------------------------- */
    var heroNav = t.closest("[data-mm-hero-nav]");
    if (heroNav) {
      var hero = heroNav.closest("[data-mm-hero]");
      if (hero) stepHero(hero, Number(heroNav.getAttribute("data-mm-hero-nav")));
      return;
    }

    /* -- product card media carousel ------------------------------------- */
    var cardNav = t.closest("[data-mm-card-nav]");
    if (cardNav) {
      event.preventDefault();
      event.stopPropagation();
      var media = cardNav.closest("[data-mm-card-media]");
      if (media) stepCardMedia(media, Number(cardNav.getAttribute("data-mm-card-nav")));
      return;
    }

    /* -- quick add ------------------------------------------------------- */
    var quickAdd = t.closest("[data-mm-quickadd]");
    if (quickAdd) {
      event.preventDefault();
      event.stopPropagation();
      openQuickAdd(quickAdd.getAttribute("data-mm-quickadd"));
      return;
    }

    if (t.closest("[data-mm-modal-close]") || t.closest(".mm-modal-scrim")) { closeQuickAdd(); return; }

    /* -- size chips (quick add + PDP) ------------------------------------ */
    var sizeBtn = t.closest("[data-mm-size]");
    if (sizeBtn && !sizeBtn.disabled) {
      var group = sizeBtn.closest("[data-mm-size-group]");
      if (group) {
        $$("[data-mm-size]", group).forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
        sizeBtn.setAttribute("aria-pressed", "true");
        var variantId = sizeBtn.getAttribute("data-mm-variant");
        var scope = group.closest("[data-mm-buy-scope]") || document;
        var input = $("[data-mm-variant-input]", scope);
        if (input) input.value = variantId;
        var submit = $("[data-mm-submit]", scope);
        if (submit) {
          submit.disabled = false;
          var ready = submit.getAttribute("data-mm-ready-label");
          if (ready) submit.textContent = ready;
        }
        var hint = $("[data-mm-size-hint]", scope);
        if (hint) hint.style.display = "none";
      }
      return;
    }

    /* -- quick add submit ------------------------------------------------ */
    var qaSubmit = t.closest("[data-mm-quickadd-submit]");
    if (qaSubmit && !qaSubmit.disabled) {
      var qaScope = qaSubmit.closest("[data-mm-buy-scope]");
      var qaField = qaScope ? $("[data-mm-variant-input]", qaScope) : null;
      var qaVariant = qaField ? qaField.value : null;
      if (!qaVariant) return;
      qaSubmit.disabled = true;
      addToCart(qaVariant, 1)
        .then(function () { flash(qaSubmit, "Added to bag"); setTimeout(closeQuickAdd, 700); })
        .catch(function (err) { flash(qaSubmit, err.message || "Sold out", 2400); })
        .then(function () { window.setTimeout(function () { qaSubmit.disabled = false; }, 800); });
      return;
    }

    /* -- quantity stepper ------------------------------------------------ */
    var qty = t.closest("[data-mm-qty]");
    if (qty) {
      var qtyWrap = qty.closest(".mm-qty");
      var qtyInput = qtyWrap ? $("input", qtyWrap) : null;
      if (qtyInput) {
        var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + Number(qty.getAttribute("data-mm-qty")));
        qtyInput.value = next;
      }
      return;
    }

    /* -- accordions (PDP) ------------------------------------------------ *
     * Superseded by mm-accordion.js, which owns .mm-acc-head on the capture
     * phase and stops the event immediately, so this branch is unreachable
     * for those clicks. */
    var acc = t.closest(".mm-acc-head");
    if (acc) {
      var accOpen = acc.getAttribute("aria-expanded") === "true";
      var accGroup = acc.closest(".mm-acc");
      if (accGroup && !accOpen) {
        $$(".mm-acc-head", accGroup).forEach(function (h) {
          h.setAttribute("aria-expanded", "false");
          if (h.nextElementSibling) h.nextElementSibling.setAttribute("data-open", "false");
        });
      }
      acc.setAttribute("aria-expanded", String(!accOpen));
      if (acc.nextElementSibling) acc.nextElementSibling.setAttribute("data-open", String(!accOpen));
      return;
    }

    /* -- facet collapse (PLP sidebar) ------------------------------------ */
    var facet = t.closest(".mm-facet-head");
    if (facet) {
      var facetOpen = facet.getAttribute("aria-expanded") === "true";
      facet.setAttribute("aria-expanded", String(!facetOpen));
      return;
    }

    /* -- filter sidebar toggle ------------------------------------------- */
    var filterToggle = t.closest("[data-mm-filter-toggle]");
    if (filterToggle) {
      var body = $("[data-mm-plp-body]");
      if (body) {
        var on = body.getAttribute("data-filters") === "true";
        body.setAttribute("data-filters", String(!on));
        var lbl = $("[data-mm-filter-label]", filterToggle);
        if (lbl) lbl.textContent = on ? "Show Filters" : "Hide Filters";
        try { localStorage.setItem("mm-filters-open", String(!on)); } catch (e) {}
      }
      return;
    }

    /* -- shop this look --------------------------------------------------- */
    if (t.closest("[data-mm-look-open]")) { setLook(true); return; }
    if (t.closest("[data-mm-look-close]") || t.closest(".mm-look-scrim")) { setLook(false); return; }

    var lookAdd = t.closest("[data-mm-look-add]");
    if (lookAdd && !lookAdd.disabled) {
      lookAdd.disabled = true;
      addToCart(lookAdd.getAttribute("data-mm-look-add"), 1)
        .then(function () { flash(lookAdd, "Added"); })
        .catch(function () { flash(lookAdd, "Unavailable"); })
        .then(function () { window.setTimeout(function () { lookAdd.disabled = false; }, 900); });
      return;
    }

    var lookAll = t.closest("[data-mm-look-add-all]");
    if (lookAll && !lookAll.disabled) {
      var ids = (lookAll.getAttribute("data-mm-look-add-all") || "").split(",").filter(Boolean);
      if (!ids.length) return;
      lookAll.disabled = true;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) })
      }).then(function () { refreshCartCount(); flash(lookAll, "Added to bag"); setTimeout(function () { setLook(false); }, 700); })
        .catch(function () { flash(lookAll, "Something went wrong"); })
        .then(function () { window.setTimeout(function () { lookAll.disabled = false; }, 900); });
      return;
    }
  });

  /* -- PDP add to bag ------------------------------------------------------ */
  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!form.matches || !form.matches("[data-mm-product-form]")) return;
    event.preventDefault();
    var submit = $("[data-mm-submit]", form);
    var variant = $("[data-mm-variant-input]", form);
    var quantity = $("[data-mm-qty-input]", form);
    if (!variant || !variant.value) return;
    if (submit) submit.disabled = true;
    addToCart(variant.value, quantity ? parseInt(quantity.value, 10) || 1 : 1)
      .then(function () { flash(submit, "Added to bag"); })
      .catch(function (err) { flash(submit, err.message || "Unavailable", 2400); })
      .then(function () { window.setTimeout(function () { if (submit) submit.disabled = false; }, 900); });
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    closeQuickAdd();
    setDrawer(false);
    setLook(false);
  });

  /* -- selects auto-submit ------------------------------------------------- */
  document.addEventListener("change", function (event) {
    var el = event.target;
    if (!el.matches) return;
    if (el.matches("[data-mm-sort]") || el.matches("[data-mm-facet-input]")) {
      var form = el.closest("form");
      if (form) form.submit();
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Component behaviour                                                     */
  /* ---------------------------------------------------------------------- */

  function setDrawer(open) {
    var root = $("[data-mm-drawer]");
    if (!root) return;
    root.setAttribute("data-open", String(open));
    document.body.classList.toggle("mm-no-scroll", open);
  }

  function setLook(open) {
    var panel = $("[data-mm-look]");
    var scrim = $("[data-mm-look-scrim]");
    if (panel) panel.setAttribute("data-open", String(open));
    if (scrim) scrim.setAttribute("data-open", String(open));
    document.body.classList.toggle("mm-no-scroll", open);
  }

  function stepHero(hero, delta) {
    var slides = $$("[data-mm-hero-slide]", hero);
    var copies = $$("[data-mm-hero-copy]", hero);
    if (slides.length < 2) return;
    var current = 0;
    slides.forEach(function (s, i) { if (s.getAttribute("data-active") === "true") current = i; });
    var next = (current + delta + slides.length) % slides.length;
    slides.forEach(function (s, i) { s.setAttribute("data-active", String(i === next)); });
    copies.forEach(function (c, i) {
      var on = i === next;
      c.setAttribute("data-active", String(on));
      if (on) {
        c.classList.remove("mm-animate-rise");
        void c.offsetWidth;
        c.classList.add("mm-animate-rise");
      }
    });
  }

  function stepCardMedia(media, delta) {
    var slides = $$("[data-mm-card-slide]", media);
    if (slides.length < 2) return;
    var current = 0;
    slides.forEach(function (s, i) { if (s.getAttribute("data-active") === "true") current = i; });
    var next = (current + delta + slides.length) % slides.length;
    slides.forEach(function (s, i) {
      var on = i === next;
      s.setAttribute("data-active", String(on));
      var video = s.querySelector("video");
      if (video) { if (on) { video.play().catch(function () {}); } else { video.pause(); } }
    });
  }

  function openQuickAdd(handle) {
    var modal = $("[data-mm-modal]");
    if (!modal || !handle) return;
    var panel = $("[data-mm-modal-panel]", modal);
    panel.innerHTML = '<p class="mm-eyebrow mm-muted" style="padding:2rem 0;text-align:center">Loading…</p>';
    modal.setAttribute("data-open", "true");
    document.body.classList.add("mm-no-scroll");

    fetch("/products/" + handle + "?view=mm-quickadd", { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) { return r.text(); })
      .then(function (html) { panel.innerHTML = html; })
      .catch(function () {
        panel.innerHTML = '<p class="mm-eyebrow mm-muted" style="padding:2rem 0;text-align:center">Could not load this product.</p>';
      });
  }

  function closeQuickAdd() {
    var modal = $("[data-mm-modal]");
    if (!modal) return;
    modal.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
  }

  function syncHeaderHeight() {
    var root = $("[data-mm-header]");
    if (!root) return;
    var h = Math.round(root.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--mm-header-h", h + "px");
  }

  function onScroll() {
    var root = $("[data-mm-header]");
    if (!root) return;
    root.setAttribute("data-scrolled", String(window.scrollY > 20));
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                    */
  /* ---------------------------------------------------------------------- */

  var revealObserver = null;

  function initReveal() {
    if (!("IntersectionObserver" in window)) {
      $$(".mm-reveal").forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var delay = entry.target.getAttribute("data-mm-delay");
          if (delay) entry.target.style.animationDelay = delay + "ms";
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: "0px 0px -10% 0px", threshold: 0.08 });
    }
    $$(".mm-reveal:not(.is-visible)").forEach(function (el) { revealObserver.observe(el); });
  }

  function initCurtain() {
    var curtain = $("[data-mm-curtain]");
    if (!curtain) return;
    var seen = false;
    try { seen = sessionStorage.getItem("mm-curtain-seen") === "1"; } catch (e) {}
    if (seen) { curtain.remove(); return; }
    try { sessionStorage.setItem("mm-curtain-seen", "1"); } catch (e) {}
    window.setTimeout(function () { if (curtain.parentNode) curtain.remove(); }, 2100);
  }

  function initAnnounce() {
    var closed = false;
    try { closed = sessionStorage.getItem("mm-announce-closed") === "1"; } catch (e) {}
    if (!closed) return;
    var bar = $("[data-mm-announce]");
    if (bar) bar.remove();
  }

  /* Restores the shopper's last filter-drawer state. Runs on every navigation,
     because the server always renders the PLP with the drawer closed. */
  function initFilterPreference() {
    var body = $("[data-mm-plp-body]");
    if (!body) return;
    var stored = null;
    try { stored = localStorage.getItem("mm-filters-open"); } catch (e) {}
    if (stored === null) return;
    body.setAttribute("data-filters", stored);
    var lbl = $("[data-mm-filter-label]");
    if (lbl) lbl.textContent = stored === "true" ? "Hide Filters" : "Show Filters";
  }

  function initVideos() {
    $$("[data-mm-autoplay]").forEach(function (video) {
      video.muted = true;
      var play = video.play();
      if (play && play.catch) play.catch(function () {});
    });
  }

  /* ---------------------------------------------------------------------- */
  /* PDP colourway swatches                                                  */
  /* ---------------------------------------------------------------------- */

  function ensureSwatchStyles(host) {
    if ($("link[data-mm-swatch-css]")) return;

    var href = (host && host.getAttribute("data-css")) || "";
    if (!href && SELF_SRC) href = SELF_SRC.replace("mm-editorial.js", "mm-swatch.css");
    if (!href || href.indexOf("mm-swatch.css") === -1) return;

    var link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-mm-swatch-css", "1");
    document.head.appendChild(link);
  }

  /* The title base — everything before ' :: ' — reconstructed from the two
     values the section already publishes: the heading is the display name and
     the eyebrow is the fabric. "Moreland Travel Blazer - OrigamiTech". */
  function titleBase(host) {
    var info = host.closest(".mm-pdp-info") || document;
    var heading = $("h1", info);
    var eyebrow = $(".mm-eyebrow.mm-muted", info);
    if (!heading) return "";

    var name = (heading.textContent || "").trim();
    var fabric = eyebrow ? (eyebrow.textContent || "").trim() : "";
    if (!name) return "";
    return fabric ? name + " - " + fabric : name;
  }

  /* Every key worth asking about, queried together and merged.
   *
   * Neither key is trustworthy alone. The catalogue's `YGroup_*` tag looks
   * authoritative but is applied to the wrong set on at least the Moreland: it
   * marks the retired Scuba and 1.0 colourways while the current OrigamiTech
   * range carries no tag at all. Treating it as the sole key made it exclusive,
   * so Dark Navy resolved to three colours while Black resolved to six.
   *
   * The title base covers the current range but misses any style whose
   * colourways were named inconsistently. Both are asked and the answers
   * merged; the union can only be more complete than either alone.
   */
  function groupKeysFor(host) {
    var keys = [];
    var ygroup = (host.getAttribute("data-ygroup") || "").trim();
    if (ygroup) keys.push(ygroup);

    var base = titleBase(host);
    if (base && keys.indexOf(base) === -1) keys.push(base);

    return keys;
  }

  function initColorSiblings() {
    var host = $("[data-mm-colors]");
    if (!host || host.getAttribute("data-mm-done") === "1") return;
    host.setAttribute("data-mm-done", "1");

    var cfg = {
      keys: groupKeysFor(host),
      parent: host.getAttribute("data-parent") || "",
      fabric: (host.getAttribute("data-fabric") || "").toLowerCase(),
      current: (host.getAttribute("data-current") || "").toLowerCase(),
      hideSoldOut: host.getAttribute("data-hide-soldout") !== "false",
      hideSingle: host.getAttribute("data-hide-single") !== "false",
      newDays: parseInt(host.getAttribute("data-new-days"), 10) || 0,
      lowStock: parseInt(host.getAttribute("data-low-stock"), 10) || 0
    };

    /* The map is read for two things: the hex fill, and the ORDER.
     *
     * Without a canonical order the row is however the search endpoint happened
     * to rank that query, which differs per colourway — so Black and Dark Navy
     * showed the same six colours in two different sequences and the swatches
     * appeared to shuffle as you moved between them. The map's line order is
     * the merchandising sequence and is the same on every product, so it makes
     * the row stable everywhere.
     */
    var map = {};
    var mapOrder = {};
    var seq = 0;
    (host.getAttribute("data-map") || "").split("\n").forEach(function (line) {
      var bits = line.split("|");
      if (bits.length < 2) return;
      var key = bits[0].trim().toLowerCase();
      if (!key) return;
      map[key] = bits[1].trim();
      if (!(key in mapOrder)) { mapOrder[key] = seq; seq += 1; }
    });
    cfg.order = mapOrder;

    var lookup = cfg.keys.length
      ? Promise.all(cfg.keys.map(function (k) {
          /* One key failing must not lose the other's results. */
          return fetchGroup(k).catch(function () { return []; });
        })).then(function (sets) {
          var merged = [];
          sets.forEach(function (s) { merged = merged.concat(s || []); });
          return merged.length >= 2 ? merged : fetchPredictive(cfg);
        })
      : fetchPredictive(cfg);

    lookup
      .catch(function () { return fetchPredictive(cfg); })
      .then(function (colors) { render(colors, cfg, map, host); })
      .catch(function () { /* leave the row hidden */ });
  }

  function fetchGroup(group) {
    var url = "/search?type=product&view=mm-swatches&q=" + encodeURIComponent(group);
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) { return (data && data.colors) || []; });
  }

  /* Last resort. `unavailable_products=hide` is the one lever predictive search
     offers here — it used to be set to `last`, which is precisely why sold-out
     colourways stayed in the row. It cannot report stock or publish dates, so
     a row resolved this way carries no badges. */
  function fetchPredictive(cfg) {
    if (!cfg.parent) return Promise.resolve([]);
    var query = encodeURIComponent(cfg.parent + (cfg.fabric ? " " + cfg.fabric : ""));
    var unavailable = cfg.hideSoldOut ? "hide" : "last";
    var url = "/search/suggest.json?q=" + query +
      "&resources[type]=product&resources[limit]=10" +
      "&resources[options][unavailable_products]=" + unavailable;

    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        var parentKey = cfg.parent.toLowerCase();
        return products.filter(function (p) {
          var t = (p.title || "").toLowerCase();
          if (t.indexOf(" :: ") === -1) return false;
          if (t.indexOf(parentKey) === -1) return false;
          if (cfg.fabric && t.indexOf(cfg.fabric) === -1) return false;
          return true;
        }).map(function (p) {
          return {
            name: p.title.split(" :: ").pop().trim(),
            url: p.url,
            image: p.image || "",
            /* Predictive search omits `available` on some shop plans; treat a
               missing value as in stock, since the `hide` option above has
               already filtered the sold-out ones server-side. */
            available: p.available !== false,
            qty: 0,
            published: 0,
            badge: ""
          };
        });
      });
  }

  /* An explicit `Badge:` tag from the catalogue always wins. Otherwise New is
     tested before Last Few — a colour that has just landed in small numbers
     reads better as new than as nearly gone. */
  function badgeFor(color, cfg) {
    if (color.badge) return color.badge;
    var nowSec = Math.floor(Date.now() / 1000);
    if (cfg.newDays > 0 && color.published > 0) {
      var ageDays = (nowSec - color.published) / 86400;
      if (ageDays <= cfg.newDays) return "New";
    }
    if (cfg.lowStock > 0 && color.qty > 0 && color.qty <= cfg.lowStock) return "Last Few";
    return "";
  }

  /* Colours repeat across products: the same colourway is returned by both
     group keys, and the catalogue also carries separate extended-size entries
     such as "Black 00 and 20" holding their own stock. Keeping only the first
     hit would let a 1-unit size-run entry brand the main colourway "Last Few"
     while hundreds sit in the other record, so duplicates are merged: stock is
     summed, and availability and the earliest publish date carried over. */
  function dedupe(colors) {
    var byName = {};
    var order = [];

    colors.forEach(function (c) {
      if (!c || !c.name) return;
      var key = c.name.toLowerCase();
      var seen = byName[key];
      if (!seen) {
        byName[key] = {
          name: c.name,
          url: c.url,
          image: c.image,
          available: c.available !== false,
          qty: c.qty || 0,
          published: c.published || 0,
          badge: c.badge || ""
        };
        order.push(key);
        return;
      }
      /* Same record arriving from a second key must not double its stock. */
      if (c.url && c.url === seen.url) return;
      seen.qty += c.qty || 0;
      if (c.available !== false) {
        seen.available = true;
        if (!seen.url) seen.url = c.url;
      }
      if (!seen.image && c.image) seen.image = c.image;
      if (!seen.badge && c.badge) seen.badge = c.badge;
      if (c.published && (!seen.published || c.published < seen.published)) {
        seen.published = c.published;
      }
    });

    return order.map(function (k) { return byName[k]; });
  }

  /* Canonical swatch order: the colour map's line order, then anything not in
     the map alphabetically after it. Alphabetical alone would be stable but
     arbitrary — it would put Black next to Blush and split the neutrals. */
  function sortByMap(list, order) {
    var UNLISTED = 10000;
    return list.slice().sort(function (a, b) {
      var ai = order[a.name.toLowerCase()];
      var bi = order[b.name.toLowerCase()];
      if (ai === undefined) ai = UNLISTED;
      if (bi === undefined) bi = UNLISTED;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
  }

  function render(colors, cfg, map, host) {
    if (!colors) return;

    var list = dedupe(colors).filter(function (c) {
      /* The colour being viewed always stays in the row even when it is sold
         out — removing it would leave the swatch strip with no current state
         and no way back to where the shopper is standing. */
      if (cfg.hideSoldOut && c.available === false && c.name.toLowerCase() !== cfg.current) return false;
      return true;
    });

    if (!list.length) return;
    if (list.length < 2 && cfg.hideSingle) return;

    list = sortByMap(list, cfg.order || {});

    ensureSwatchStyles(host);

    var listEl = $("[data-mm-colors-list]", host);
    var nameEl = $("[data-mm-color-name]", host);
    if (!listEl) return;
    listEl.innerHTML = "";

    var anyBadge = false;

    list.forEach(function (c) {
      var key = c.name.toLowerCase();
      var isCurrent = key === cfg.current;
      var soldOut = c.available === false;
      var node = document.createElement(isCurrent || soldOut ? "span" : "a");
      if (!isCurrent && !soldOut) node.href = c.url;
      node.setAttribute("aria-current", String(isCurrent));
      node.setAttribute("title", c.name);
      node.setAttribute("aria-label", c.name + (soldOut ? " — sold out" : ""));
      if (soldOut) node.setAttribute("data-mm-soldout", "true");

      var badge = badgeFor(c, cfg);
      if (badge) {
        node.setAttribute("data-mm-badge", badge);
        anyBadge = true;
      }

      if (map[key]) {
        node.style.backgroundColor = map[key];
      } else if (c.image) {
        node.style.backgroundImage = 'url("' + c.image + '")';
      } else {
        node.style.backgroundColor = "#d8d2c8";
      }

      node.addEventListener("mouseenter", function () { if (nameEl) nameEl.textContent = c.name; });
      node.addEventListener("mouseleave", function () {
        if (nameEl) nameEl.textContent = host.getAttribute("data-current");
      });
      listEl.appendChild(node);
    });

    listEl.setAttribute("data-has-badges", String(anyBadge));
    host.removeAttribute("hidden");
  }

  function mmInit() {
    initAnnounce();
    initCurtain();
    syncHeaderHeight();
    onScroll();
    initReveal();
    initFilterPreference();
    initVideos();
    initColorSiblings();
    refreshCartCount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mmInit);
  } else {
    mmInit();
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", syncHeaderHeight, { passive: true });
  document.addEventListener("shopify:section:load", mmInit);
  document.addEventListener("platter-cache-busted", mmInit);

  /* Barba swaps <main>; re-run init once the new container is in the DOM. */
  if (window.barba && window.barba.hooks) {
    window.barba.hooks.after(function () { mmInit(); });
  } else {
    document.addEventListener("barba:after", mmInit);
  }

  window.mmInit = mmInit;
})();
